import { useCallback } from 'react';
import { useDeleteTask, useUpdateTask, useTaskCreation } from '../../../../hooks/queries/useTasks';
import { useTaskUIStore } from '../../../../store/taskUIStore';
import { useTaskErrorHandlers } from './useTaskErrorHandler';
import { useVoiceRecording } from '../../../../hooks/useVoiceRecording';
import { logger } from '../../../../utils/logger';
import type { TaskItem } from '../../../../types/topic';

/**
 * Shared task operation handlers
 * Provides consistent task operations across desktop and mobile components
 */
export const useTaskHandlers = (topicId: string) => {
  // React Query mutations
  const deleteTaskMutation = useDeleteTask();
  const updateTaskMutation = useUpdateTask();
  const { createTask, formatTaskText } = useTaskCreation();
  
  // Voice recording
  const { isRecording, startRecording, stopRecording } = useVoiceRecording();
  
  // UI state actions
  const { setItemToDelete, setRecordingColumn } = useTaskUIStore();
  
  // Error handlers
  const {
    handleTaskCreationError,
    handleTaskUpdateError,
    handleTaskDeletionError,
    handleTaskMoveError,
    handleVoiceError
  } = useTaskErrorHandlers();

  // Task deletion handler
  const handleTaskDeletion = useCallback(
    async (taskId: string) => {
      try {
        await deleteTaskMutation.mutateAsync({ topicId, taskId });
        setItemToDelete(null);
      } catch (error) {
        handleTaskDeletionError(error, { taskId, topicId });
      }
    },
    [topicId, deleteTaskMutation, setItemToDelete, handleTaskDeletionError]
  );

  // Task update handler
  const handleTaskUpdate = useCallback(
    async (taskId: string, updates: Partial<TaskItem>) => {
      try {
        logger.component('Updating task', { taskId, ...updates });
        
        await updateTaskMutation.mutateAsync({
          topicId,
          taskId,
          updates: {
            title: updates.title,
            description: updates.description,
            status: updates.status,
            tags: updates.tags,
            deadline: updates.deadline,
            assignedTo: updates.assigned_to,
            isHighlighted: updates.is_highlighted,
          }
        });
        
        logger.component('Task updated successfully');
      } catch (error) {
        handleTaskUpdateError(error, { taskId, topicId, updates });
      }
    },
    [topicId, updateTaskMutation, handleTaskUpdateError]
  );

  // Voice recording handler
  const handleVoiceInput = useCallback(
    async (columnId: TaskItem['status']) => {
      if (isRecording) {
        try {
          const result = await stopRecording();
          const text = result?.transcription || '';

          if (text) {
            const formattedText = formatTaskText(text);
            
            // Create task using explicit data passing (consistent pattern)
            await createTask(columnId, {
              title: formattedText,
              description: '',
              tags: [],
              deadline: null,
              assignedTo: null,
              topicId: topicId
            });
          }
          setRecordingColumn(null);
        } catch (error) {
          handleVoiceError(error, { topicId, columnId, operation: 'voice_processing' });
        }
      } else {
        try {
          await startRecording();
          setRecordingColumn(columnId);
        } catch (error) {
          handleVoiceError(error, { topicId, columnId, operation: 'voice_recording' });
        }
      }
    },
    [isRecording, startRecording, stopRecording, setRecordingColumn, formatTaskText, createTask, topicId, handleVoiceError]
  );

  // Task creation handler
  const handleTaskCreation = useCallback(
    async (columnId: TaskItem['status'], taskData: {
      title: string;
      description?: string;
      tags?: string[];
      deadline?: string | null;
      assignedTo?: string | null;
    }) => {
      try {
        await createTask(columnId, {
          ...taskData,
          topicId: topicId
        });
      } catch (error) {
        handleTaskCreationError(error, { topicId, columnId, title: taskData.title });
      }
    },
    [createTask, topicId, handleTaskCreationError]
  );

  return {
    // Task operations
    handleTaskDeletion,
    handleTaskUpdate,
    handleTaskCreation,
    handleVoiceInput,
    
    // Voice state
    isRecording,
    
    // Error handlers for special cases
    handleTaskMoveError,
    
    // Direct access to mutation state for loading indicators
    isDeleting: deleteTaskMutation.isPending,
    isUpdating: updateTaskMutation.isPending,
  };
};